import { useEffect, useState } from "react";

import {
  connectServer,
  getServer,
  listSecrets,
  type AuthEnvelope,
  type AuthMethod,
  type OpenAPISpecInput,
  type Secret,
  type Server,
  type StdioArgItem,
} from "@/lib/api";
import type { Connector } from "@/lib/connectorCatalog";
import {
  buildCurlFromForm,
  errMsg,
  Header,
  HTTPMethod,
  METHOD_LABELS,
  SERVER_NAME_HINT,
  SERVER_NAME_RE,
  URL_RE,
} from "./shared";

export type ConnectKind = "http" | "openapi" | "stdio";
export type OpenAPISource = "spec" | "form" | "curl";

// EnvRow is one row of the unified stdio env-var editor. A row's type is derived
// from where its value lives: a "plaintext" row is the inline sealed env
// (StdioEnv); a "secret" row is a vault attachment (SecretRef, mode env|file).
// The two never share a name (enforced on submit + server-side).
export type EnvRow =
  | { type: "plaintext"; key: string; value: string }
  | { type: "secret"; varName: string; secretId: string; mode: "env" | "file" };

export interface ConnectFormOpts {
  reconnect?: Server | null;
  prefill?: Connector | null;
  edit?: Server | null;
  onConnected: (serverName: string, building?: boolean) => void;
  onError: (msg: string) => void;
  // When set, an OAuth handoff opens a popup (host stays on the page) instead of
  // a full-page redirect.
  onOAuth?: (authorizeUrl: string, serverName: string, popup: Window | null) => void;
}

// useConnectForm owns ALL connect-an-integration state, validation, and submit
// logic. The UI (wizard page + reconnect/edit dialog) renders from its return.
export function useConnectForm({
  reconnect = null,
  prefill = null,
  edit = null,
  onConnected,
  onError,
  onOAuth,
}: ConnectFormOpts) {
  const seed = reconnect
    ? { name: reconnect.name, url: reconnect.upstream_url, method: "oauth2_authorization_code" as AuthMethod, scopes: "" }
    : edit
      ? { name: edit.name, url: edit.upstream_url, method: (edit.auth_method || "none") as AuthMethod, scopes: "" }
      : prefill
        ? {
            name: prefill.id,
            url: prefill.mcpUrl ?? "",
            method: prefill.method ?? ("oauth2_authorization_code" as AuthMethod),
            scopes: (prefill.scopes ?? []).join(" "),
          }
        : null;

  const freshConnect = !reconnect && !prefill && !edit;
  const isEdit = !!edit;
  const showKindFields = freshConnect || isEdit;

  const [kind, setKind] = useState<ConnectKind>(
    edit?.kind === "openapi" || edit?.kind === "stdio" ? edit.kind : "http",
  );
  // The catalog connector currently applied (seed or in-wizard pick), so the
  // needs-OAuth-app banner can render in both entry paths.
  const [connector, setConnector] = useState<Connector | null>(prefill);

  const [stdioRuntime, setStdioRuntime] = useState<"npm" | "python">(edit?.runtime === "python" ? "python" : "npm");
  const [stdioPkg, setStdioPkg] = useState(edit?.package ?? "");
  const [stdioVersion, setStdioVersion] = useState(edit?.version ?? "");
  // envRows is the single unified state behind the env-var editor: plaintext rows
  // (inline sealed env) and secret rows (vault attachments) in one typed list. A
  // fresh connect starts with one empty plaintext row to type into.
  const [envRows, setEnvRows] = useState<EnvRow[]>([{ type: "plaintext", key: "", value: "" }]);

  const [availSecrets, setAvailSecrets] = useState<Secret[]>([]);
  useEffect(() => {
    let live = true;
    listSecrets()
      .then((s) => live && setAvailSecrets(s))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const isStdioEdit = !!edit && edit.kind === "stdio";
  const [envEditReady, setEnvEditReady] = useState(!isStdioEdit);
  const [envEditError, setEnvEditError] = useState<string | null>(null);
  const [stdioArgRows, setStdioArgRows] = useState<
    { value: string; locked?: boolean; redacted?: string; keepIndex?: number }[]
  >([]);
  useEffect(() => {
    if (!isStdioEdit || !edit) return;
    let cancelled = false;
    const ENV_LOAD_ERR =
      "Couldn't load this integration's environment variables or arguments. Saving keeps them unchanged — reopen to edit them.";
    getServer(edit.name)
      .then((d) => {
        if (cancelled) return;
        if (d.env_unavailable) {
          setEnvEditError(ENV_LOAD_ERR);
        } else {
          // Seed the unified editor: plaintext rows from env_keys+env_values (the
          // value is prefilled — env_values shares its keyset with env_keys, so a
          // value is always present), then secret rows from attached_secrets.
          const keys = d.env_keys ?? [];
          const values = d.env_values ?? {};
          const plaintextRows: EnvRow[] = keys.map((k) => ({ type: "plaintext", key: k, value: values[k] ?? "" }));
          const secretRows: EnvRow[] = (d.attached_secrets ?? []).map((r) => ({
            type: "secret",
            varName: r.var_name,
            secretId: r.secret_id,
            mode: r.mode,
          }));
          const seeded = [...plaintextRows, ...secretRows];
          setEnvRows(seeded.length > 0 ? seeded : [{ type: "plaintext", key: "", value: "" }]);
          const redacted = d.args_redacted ?? [];
          setStdioArgRows(redacted.map((r, i) => ({ value: "", locked: true, redacted: r, keepIndex: i })));
        }
      })
      .catch(() => {
        if (!cancelled) setEnvEditError(ENV_LOAD_ERR);
      })
      .finally(() => {
        if (!cancelled) setEnvEditReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [edit, isStdioEdit]);

  const [stdioArgs, setStdioArgs] = useState("");

  const editSource: OpenAPISource | null = edit
    ? edit.spec_url || edit.spec_text
      ? "spec"
      : edit.curl
        ? "curl"
        : null
    : null;
  const [source, setSource] = useState<OpenAPISource>(editSource ?? "form");
  const [specUrl, setSpecUrl] = useState(edit?.spec_url ?? "");
  const [specText, setSpecText] = useState(edit?.spec_text ?? "");
  const [curl, setCurl] = useState(edit?.curl ?? "");
  const [baseUrl, setBaseUrl] = useState(edit?.base_url ?? "");

  const [formMethod, setFormMethod] = useState<HTTPMethod>("GET");
  const [formUrl, setFormUrl] = useState("");
  const [formHeaders, setFormHeaders] = useState<Header[]>([{ key: "", value: "" }]);
  const [formBody, setFormBody] = useState("");
  const [instructions, setInstructions] = useState(edit?.instructions ?? "");

  const [name, setName] = useState(seed?.name ?? "");
  const [url, setUrl] = useState(seed?.url ?? "");
  const [method, setMethod] = useState<AuthMethod>(seed?.method ?? "none");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [token, setToken] = useState("");
  const [apiHeader, setApiHeader] = useState("X-API-Key");
  const [apiValue, setApiValue] = useState("");
  const [basicUser, setBasicUser] = useState("");
  const [basicPass, setBasicPass] = useState("");
  const [headers, setHeaders] = useState<Header[]>([{ key: "", value: "" }]);
  const [tokenURL, setTokenURL] = useState("");
  const [clientID, setClientID] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [scopes, setScopes] = useState(seed?.scopes ?? "");

  const isOpenAPI = showKindFields && kind === "openapi";
  const isStdio = showKindFields && kind === "stdio";

  const methodOptions = (Object.keys(METHOD_LABELS) as AuthMethod[]).filter(
    (m) => (!isOpenAPI && !isStdio) || m !== "oauth2_authorization_code",
  );

  const nameValid = SERVER_NAME_RE.test(name.trim());
  const nameError = !reconnect && name.length > 0 && !nameValid ? SERVER_NAME_HINT : null;
  const urlValid = URL_RE.test(url.trim());
  const urlError = url.length > 0 && !urlValid ? "Enter a URL starting with http:// or https://" : null;

  const formUrlValid = URL_RE.test(formUrl.trim());
  const hasSpecSource =
    source === "curl"
      ? curl.trim().length > 0
      : source === "form"
        ? formUrlValid
        : specUrl.trim().length > 0 || specText.trim().length > 0;
  const submittable = isOpenAPI
    ? nameValid && hasSpecSource
    : isStdio
      ? nameValid && stdioPkg.trim().length > 0 && envEditReady
      : nameValid && urlValid;

  // applyConnector seeds a fresh wizard connect from a catalog pick (HTTP/OAuth).
  function applyConnector(c: Connector) {
    setConnector(c);
    setKind("http");
    setName(c.id);
    setUrl(c.mcpUrl ?? "");
    setMethod(c.method ?? "oauth2_authorization_code");
    setScopes((c.scopes ?? []).join(" "));
  }

  // chooseManual switches to a hand-configured connect of the given kind, clearing
  // any prior catalog pick + its seeded name/url so nothing stale carries over.
  function chooseManual(k: ConnectKind) {
    setConnector(null);
    setKind(k);
    setName("");
    setUrl("");
    setScopes("");
    setMethod(k === "http" ? "none" : k === "openapi" ? "bearer" : "none");
  }

  function buildAuth(): AuthEnvelope {
    if (isStdio) return { method: "none" };
    switch (method) {
      case "none":
        return { method };
      case "bearer":
        return { method, bearer: { token } };
      case "api_key":
        return { method, api_key: { header: apiHeader, value: apiValue } };
      case "basic":
        return { method, basic: { username: basicUser, password: basicPass } };
      case "headers":
        return {
          method,
          headers: Object.fromEntries(headers.filter((h) => h.key.trim()).map((h) => [h.key.trim(), h.value])),
        };
      case "oauth2_client_credentials":
        return {
          method,
          oauth2_cc: {
            token_url: tokenURL,
            client_id: clientID,
            client_secret: clientSecret || undefined,
            scopes: scopes.trim() ? scopes.trim().split(/\s+/) : undefined,
          },
        };
      case "oauth2_authorization_code":
        return {
          method,
          oauth2_ac: {
            token_url: tokenURL || undefined,
            client_id: clientID || undefined,
            client_secret: clientSecret || undefined,
            scopes: scopes.trim() ? scopes.trim().split(/\s+/) : undefined,
          },
        };
    }
  }

  async function submit(e?: { preventDefault: () => void }) {
    e?.preventDefault();
    if (!SERVER_NAME_RE.test(name.trim())) {
      setErr(SERVER_NAME_HINT);
      return;
    }

    // npm/PyPI package (stdio): Berth builds + introspects the package; no MCP
    // URL, no OAuth. Send the package spec + sealed env/args/secret refs.
    if (isStdio) {
      if (!stdioPkg.trim()) {
        setErr(
          stdioRuntime === "python"
            ? "Enter a PyPI package name (e.g. mcp-server-fetch)."
            : "Enter an npm package name (e.g. @scope/server).",
        );
        return;
      }
      // Walk the unified env rows once: collect plaintext into env, secret rows
      // into secret_attachments, validating names + guarding name collisions
      // across both row sets (the same var can't be both a plaintext value and a
      // secret — the server overlays the secret and would shadow the plaintext).
      const env: Record<string, string> = {};
      const secretAttachments: { secret_id: string; mode: "env" | "file"; var_name: string }[] = [];
      const seenNames = new Set<string>();
      for (const row of envRows) {
        if (row.type === "plaintext") {
          const k = row.key.trim();
          if (k === "") continue;
          if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(k)) {
            setErr(`Invalid env var name "${k}" — use letters, digits, and underscores, not starting with a digit.`);
            return;
          }
          if (seenNames.has(k)) {
            setErr(`Duplicate environment variable "${k}" — each name can appear only once.`);
            return;
          }
          seenNames.add(k);
          env[k] = row.value;
        } else {
          if (!row.secretId) continue; // an unfinished "Choose a secret…" row
          const k = row.varName.trim();
          if (k === "") continue;
          if (seenNames.has(k)) {
            setErr(`Duplicate environment variable "${k}" — each name can appear only once.`);
            return;
          }
          seenNames.add(k);
          secretAttachments.push({ secret_id: row.secretId, mode: row.mode, var_name: k });
        }
      }
      // env_managed always on (fresh + edit): the form owns the FULL plaintext set
      // and re-sends every value, so the result = exactly these rows. env is sent
      // as a literal map even when {} so removing the last var clears it. The
      // env_unavailable guard suppresses env management entirely (so a decrypt
      // failure can't wipe the sealed env).
      const envFields = envEditError ? {} : { env, env_managed: true };
      let argFields: { args?: string[]; arg_items?: StdioArgItem[]; args_managed?: boolean };
      if (isStdioEdit) {
        if (envEditError) {
          argFields = {};
        } else {
          const argItems: StdioArgItem[] = [];
          for (const row of stdioArgRows) {
            if (row.value !== "") argItems.push({ value: row.value });
            else if (row.locked) argItems.push({ keep_index: row.keepIndex });
          }
          argFields = { arg_items: argItems, args_managed: true };
        }
      } else {
        const args = stdioArgs
          .split("\n")
          .map((a) => a.trim())
          .filter(Boolean);
        argFields = { args: args.length > 0 ? args : undefined };
      }
      // secret_attachments was built alongside env above (the unified row walk).
      const secretsManaged = !isStdioEdit || (envEditReady && !envEditError);
      setBusy(true);
      setErr("");
      try {
        const res = await connectServer(name.trim(), "", buildAuth(), {
          kind: "stdio",
          stdio: {
            package: stdioPkg.trim(),
            version: stdioVersion.trim() || undefined,
            runtime: stdioRuntime,
            ...envFields,
            ...argFields,
            ...(secretsManaged ? { secret_attachments: secretAttachments } : {}),
          },
        });
        onConnected(name.trim(), !res.connected);
      } catch (e2) {
        const m = errMsg(e2, "Couldn't connect the package");
        setErr(m);
        onError(m);
      } finally {
        setBusy(false);
      }
      return;
    }

    // API (openapi): no MCP discovery, so no auth-code OAuth popup.
    if (isOpenAPI) {
      if (!hasSpecSource) {
        setErr(
          source === "form"
            ? "Enter a method and a valid request URL (http:// or https://)."
            : "Provide an OpenAPI spec (URL or text) or a cURL command.",
        );
        return;
      }
      setBusy(true);
      setErr("");
      try {
        const openapi: OpenAPISpecInput = {};
        if (source === "curl") {
          if (curl.trim()) openapi.curl = curl.trim();
        } else if (source === "form") {
          openapi.curl = buildCurlFromForm(formMethod, formUrl, formHeaders, formBody);
        } else {
          if (specUrl.trim()) openapi.spec_url = specUrl.trim();
          if (specText.trim()) openapi.spec_text = specText;
        }
        if (source === "spec" && baseUrl.trim()) openapi.base_url = baseUrl.trim();
        if (source !== "spec" && instructions.trim()) openapi.instructions = instructions.trim();
        await connectServer(name.trim(), "", buildAuth(), { kind: "openapi", openapi });
        onConnected(name.trim());
      } catch (e2) {
        const m = errMsg(e2, "Couldn't connect the API");
        setErr(m);
        onError(m);
      } finally {
        setBusy(false);
      }
      return;
    }

    // HTTP/MCP. Only the auth-code flow redirects, so only it needs a popup —
    // opened synchronously inside the click (browsers block window.open after an
    // await).
    const popup =
      onOAuth && method === "oauth2_authorization_code" ? window.open("", "cb-oauth", "width=540,height=720") : null;
    setBusy(true);
    setErr("");
    try {
      const res = await connectServer(name, url, buildAuth());
      if (res.oauth_required && res.authorize_url) {
        if (onOAuth) {
          if (popup) popup.location.href = res.authorize_url;
          onOAuth(res.authorize_url, name.trim(), popup);
          return;
        }
        window.location.href = res.authorize_url;
        return;
      }
      if (popup) popup.close();
      onConnected(name.trim());
    } catch (e2) {
      if (popup) popup.close();
      const m = errMsg(e2, "Couldn't connect the integration");
      setErr(m);
      onError(m);
    } finally {
      setBusy(false);
    }
  }

  const oauthAC = !isOpenAPI && method === "oauth2_authorization_code";
  const submitLabel = busy
    ? isEdit
      ? "Saving…"
      : "Connecting…"
    : oauthAC
      ? "Continue to sign-in"
      : isEdit
        ? "Save changes"
        : "Connect";

  return {
    // identity / mode
    reconnect,
    edit,
    connector,
    freshConnect,
    isEdit,
    showKindFields,
    isStdioEdit,
    isOpenAPI,
    isStdio,
    // kind
    kind,
    setKind,
    // core
    name,
    setName,
    url,
    setUrl,
    method,
    setMethod,
    methodOptions,
    nameError,
    urlError,
    // auth fields
    token,
    setToken,
    apiHeader,
    setApiHeader,
    apiValue,
    setApiValue,
    basicUser,
    setBasicUser,
    basicPass,
    setBasicPass,
    headers,
    setHeaders,
    tokenURL,
    setTokenURL,
    clientID,
    setClientID,
    clientSecret,
    setClientSecret,
    scopes,
    setScopes,
    // openapi
    source,
    setSource,
    specUrl,
    setSpecUrl,
    specText,
    setSpecText,
    curl,
    setCurl,
    baseUrl,
    setBaseUrl,
    formMethod,
    setFormMethod,
    formUrl,
    setFormUrl,
    formUrlValid,
    formHeaders,
    setFormHeaders,
    formBody,
    setFormBody,
    instructions,
    setInstructions,
    // stdio
    stdioRuntime,
    setStdioRuntime,
    stdioPkg,
    setStdioPkg,
    stdioVersion,
    setStdioVersion,
    envRows,
    setEnvRows,
    stdioArgRows,
    setStdioArgRows,
    stdioArgs,
    setStdioArgs,
    availSecrets,
    envEditReady,
    envEditError,
    // submit
    busy,
    err,
    submittable,
    submitLabel,
    oauthAC,
    buildAuth,
    submit,
    applyConnector,
    chooseManual,
  };
}

export type ConnectForm = ReturnType<typeof useConnectForm>;
